// @todo: Migrate to 'Dialog.Drawer' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import Drawer from '@material-ui/core/Drawer';
import React, {useEffect, useState} from 'react';

import {NAV_AND_VIEW_BAR_OFFSET} from '../MultiRunWorkspace/constants';
import {
  INITIAL_DRAWER_WIDTH,
  INLINE_WORKSPACE_DRAWER_DURATION,
} from './constants';

/**
 * A custom hook that manages transition events for the floating workspace drawer.
 * It adds and removes event listeners for drawer transitions, allowing components
 * to react to drawer opening/closing animations.
 *
 * Example: you want to scroll to an item inside the drawer when it's opened.
 *
 * @param transition - The transition event to listen for (e.g. 'transitionend')
 * @param callback - Function to execute when the transition event occurs
 */
export const useFloatingWorkspaceDrawerTransition = (
  transition: string,
  callback: () => void
) => {
  useEffect(() => {
    const drawer = document.getElementById('floating-workspace-drawer');
    drawer?.addEventListener(transition, callback);
    return () => {
      const drawer = document.getElementById('floating-workspace-drawer');
      drawer?.removeEventListener(transition, callback);
    };
  }, [callback, transition]);
};

export const FloatingWorkspaceDrawer = ({
  isDrawerOpen,
  closeWorkspaceDrawer,
  children,
}: {
  isDrawerOpen: boolean;
  closeWorkspaceDrawer: () => void;
  children: React.ReactNode;
}) => {
  const [marginTop, setMarginTop] = useState(0);
  useEffect(() => {
    const updateMarginTop = () => {
      const scrollY = Math.ceil(window.scrollY);
      if (scrollY < NAV_AND_VIEW_BAR_OFFSET) {
        setMarginTop(NAV_AND_VIEW_BAR_OFFSET - scrollY);
      } else {
        setMarginTop(0);
      }
    };
    updateMarginTop();
    window.addEventListener('scroll', updateMarginTop);
    window.addEventListener('resize', updateMarginTop);
    return () => {
      window.removeEventListener('scroll', updateMarginTop);
      window.removeEventListener('resize', updateMarginTop);
    };
  }, []);

  return (
    <Drawer
      anchor="right"
      id="floating-workspace-drawer"
      open={isDrawerOpen}
      onClose={closeWorkspaceDrawer}
      variant="persistent" // renders drawer asChild
      transitionDuration={INLINE_WORKSPACE_DRAWER_DURATION} // for consistency, make sure this matches inline drawer duration
      PaperProps={{
        style: {
          marginTop: `${marginTop}px`,
          height: `calc(100% - ${marginTop}px)`,
          boxShadow: ' 0px 12px 24px 0px rgba(21, 24, 31, 0.16)',
          width: INITIAL_DRAWER_WIDTH,
        },
      }}>
      {children}
    </Drawer>
  );
};
